import { CONSTANTs } from "src/provider/config/constant"
import { ReactElement } from "react"
import { RouteData } from "src/model"
import jsSHA from 'jssha'
import access from "./localAccess"

const routeData = (path: string, name: string, component: ReactElement<any, any>): RouteData => {
    return { 
        routeProps: {
            path,
            element: component
        },
        name
    }
}

const toTitleCase = (text: string) => {
    text = text?.toLowerCase()
    return text[0]?.toUpperCase() + text?.substring(1, text.length)
}

const splitCamelCase = (text: string, isTitleCase?: boolean) => {
    let result = text.replace(/([a-z])([A-Z])/g, '$1 $2')
    result = isTitleCase ? result[0].toUpperCase() + result.substring(1) : result 
    return result
}

const getPageTitle = () => {
    let route = toTitleCase(window.location.href.split('/').pop()!)
    let pageTitle = route ? route.split('-').join(' ') : 'Hydra'
    return pageTitle
}

export const erroMessage = (text: any) => {
    const isOnline = access.getInternetStatus()
    return !isOnline ? 'No Internet Connection' : `Message: ${text || 'An error occured'}`
}

export const requestMessage = (resp: any, text = '') => {
    return resp.message || text || 'Something went wrong'
}

export const catchErrMsg = (err: any) => {
    let message = err?.response?.data?.message || 'Something went wrong, Please try again later. If problem persist kindly contact our support.'
    
    if (err.response?.data?.fieldErrors) message = retrieveAPIValidationMsg(err.response.data.fieldErrors)
    if (!message) message = err.response?.data?.message

    if (err.response?.status === 401) {
        access.getLogoutHandler()()
    }
    return message 
}

const retrieveAPIValidationMsg = (errorObj: any) => {

    return errorObj[0]?.message
}

const reportColumns = (reportName: string) => {
    const columns = {
        INSTITUTIONS_REPORT: [
            'name', 'institutionCode', 'id', 'alias', 'correspondencePhone', 'correspondenceEmail', 'setupDate'
        ],
        USERS_REPORT: [
            'id', 'firstName', 'lastName', 'email', 'username', 'emailConfirmed', 'role', 'creationDate', 'isPasswordChanged'
        ]
    }

    return columns[reportName as keyof typeof columns] || []
}

const downloadReportCSV = (data: any[], fileName: string) => {
    function convertArrayOfObjectsToCSV(data: any) {
        const csvRows = [];
        
        // Get headers from the first object
        const headers = Object.keys(data[0]);
        csvRows.push(headers.join(','));
    
        // Iterate over each object in the array
        for (const obj of data) {
            const values = headers.map(header => {
                const value = obj[header];
                // Handle cases where value might contain commas
                return typeof value === 'string' && value.includes(',') ? `"${value}"` : value;
            });
            csvRows.push(values.join(','));
        }
    
        // Combine rows into a single CSV string
        return csvRows.join('\n');
    }
    
    function downloadCSV(csvData: any, fileName: string) {
        const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
       
            const link = document.createElement('a');
            if (link.download !== undefined) {
                const url = URL.createObjectURL(blob);
                link.setAttribute('href', url);
                link.setAttribute('download', fileName);
                link.style.visibility = 'hidden';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            } else {
                // For other browsers, fallback to data URI
                const csvContent = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csvData);
                const encodedUri = encodeURI(csvContent);
                window.open(encodedUri, '_blank');
            }
        
    }
    
    const csv = convertArrayOfObjectsToCSV(data);
    downloadCSV(csv, `${fileName}.csv`);
}

export const handleTypeNumberControl = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const regex = new RegExp(/(^\d*$)|(Backspace|Tab|Delete|ArrowLeft|ArrowRight)/)
    let isValidCombo = false
    const ctrl = e.ctrlKey ? e.ctrlKey : ((e.which === 17) ? true : false); 
    const cmd = e.metaKey ? true : false
    
    if ((e.which == 86 && ctrl) || (e.which == 67 && ctrl) || (e.which == 88 && ctrl)) isValidCombo = true
    if ((e.which == 86 && cmd) || (e.which == 67 && cmd) ||  (e.which == 88 && cmd)) isValidCombo = true

    if (e.which === 38 || e.which === 40 || (!e.key.match(regex) && !isValidCombo)) {
        e.preventDefault();
    }
}

export const moneyFormat = (val: number|string, twoDecimalPlace?: boolean) => {
    var formatter = new Intl.NumberFormat('en-NG', {
        currency: 'NGN',
        minimumFractionDigits: twoDecimalPlace ? 2 : 0,
        maximumFractionDigits: 2
    })
    return formatter.format(Number(val))
}

const reactSelectStyles = () => {
    return {
        input: (style: any) => ({
            ...style,
            color: '#344054',
            fontSize: '1.6rem'
        }),
        control: (base: any, state: any) => ({
            ...base,
            background: "transparent",
            height: '4.4rem',
            border: 0,
            color: '#344054',
            fontSize: '1.6rem',
            borderColor: '#D0D5DD',
            boxShadow: 'none',
            "&:hover": {
                background: "transparent",
                borderColor: 'transparent'
            }
        }),
        option: (style: any, state: any) => {
            let color = '#000'
            if (state.isSelected) {
                color = "#0A756F";
            }

            return {
                ...style,
                color,
                paddingTop: '0.5rem',
                background: state.isSelected ? '#E7F6F5' : style.background
            }
        },
        singleValue: (style: any) => ({
            ...style,
            color: '#344054'
        }),
        menu: (base: any) => ({
            ...base,
            borderRadius: 0,
            marginTop: 0,
            margin: 0,
            padding: 0
        }),
        menuList: (base: any) => ({
            ...base,
            padding: 0
        }),
        menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
        multiValue: (styles: any) => {
            return {
                ...styles,
                backgroundColor: '#E7F6F5',
            };
        },
        multiValueLabel: (styles: any) => ({
            ...styles,
            color: '#409089',
        }),
        multiValueRemove: (styles: any) => ({
            ...styles,
            color: '#409089',
            ':hover': {
              backgroundColor: '#fee4e2',
              color: '#b42318',
            },
        }),
    };
}

export const loglite = (data: any, description?: string) => {
    if (description) console.log(`====== ${description} =======`)
    console.log(data)
}

const koboFormat = (val: number|string) => {
    let formattedValue = (Number(val) / 100)
    return Number(formattedValue).toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,');
}

const getActualKoboValue = (data: any, isKoboFormat = false) => {
    const dataKeys = Object.keys(data)
    const output: any = {}

    for (let item of dataKeys) {
        if (!isNaN(Number(data[item]))) {
            if (isKoboFormat) data[item] = Number(data[item]) * 100
            else data[item] = Number(data[item]) / 100
        }

        output[item] = data[item]
    }

    console.log({output})

    return output
}

const getUserTypeLabel = (value: string) => {
    const userType = {
        Qore: 'Admin',
        Institution: 'Client',
        ServiceProvider: 'Partner'
    }

    return userType[value as keyof typeof userType]
}

const getSignature = () => {
    const today = new Date();
    const dd = String(today.getDate()).padStart(2, '0');
    const mm = String(today.getMonth() + 1).padStart(2, '0'); // Months are zero-based
    const yyyy = today.getFullYear();

    const formattedDate = `${dd}${mm}${yyyy}`

    return hashString(`${formattedDate}${CONSTANTs.secretKey}`)
}

const hashString = (text: string) => {
    const shaObj = new jsSHA("SHA-512", "TEXT");
    shaObj.update(text)
    return shaObj.getHash("HEX")
}


export default {
    routeData,
    toTitleCase,
    getPageTitle,
    requestMessage,
    catchErrMsg,
    erroMessage,
    splitCamelCase,
    reportColumns,
    downloadReportCSV,
    moneyFormat,
    reactSelectStyles,
    koboFormat,
    getActualKoboValue,
    getUserTypeLabel,
    getSignature
}